import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase';
import SuiteHome from './SuiteHome';
import Login from './Login';
import Desktop from './Desktop';
import SuiteDashboard from './SuiteDashboard';
import DefectTrackerApp from './DefectTrackerApp';
import FeatureRequestsWrapper from './FeatureRequestsWrapper';
import NotesWrapper from './NotesWrapper';
import MessagingWrapper from './MessagingWrapper';
import AccountRequestsApp from './AccountRequestsApp';
import DiagrammingApp from './DiagrammingApp';
import IncidentReportsApp from './IncidentReportsApp';
import InjuryReportsApp from './InjuryReportsApp';
import MaintenanceRequestsApp from './MaintenanceRequestsApp';
import SopSystemApp from './SopSystemApp';
import AdminHub from './AdminHub';
import AdminPanel from './AdminPanel';
import ApplicationManager from './ApplicationManager';
import AppAccessGate from './AppAccessGate';
import OrgSettings from './OrgSettings';
import OrgSettingsAudit from './OrgSettingsAudit';
import HelpBot from './components/HelpBot';
import LoadingSpinner from './components/LoadingSpinner';

function Suite() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  // Build 25.1: org-wide settings (which suite apps the org has enabled).
  // null until the first snapshot arrives. Routes treat null as "still
  // loading" — they don't pretend the org has disabled everything.
  const [orgSettings, setOrgSettings] = useState(null);

  // Build version marker — logs on every page load so we can verify which
  // build is actually running. Update on every release.
  useEffect(() => {
    console.log('%c🏗️ SUITE BUILD: 28.0-v1.0.4', 'font-weight: bold; color: #2563eb; font-size: 14px;');
  }, []);

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // Get user profile
        const profileUnsub = onSnapshot(
          doc(db, 'users', currentUser.uid),
          (snap) => {
            if (snap.exists()) {
              setUserProfile(snap.data());
            } else {
              setUserProfile(null);
            }
            setAuthLoading(false);
          }
        );
        return () => profileUnsub();
      } else {
        setUserProfile(null);
        setAuthLoading(false);
      }
    });

    return () => unsub();
  }, []);

  // Build 25.1: org settings listener. Independent of the user — all users
  // need to know the org-wide app list to filter their dashboards.
  useEffect(() => {
    if (!user) {
      setOrgSettings(null);
      return;
    }
    const unsub = onSnapshot(
      doc(db, 'system', 'orgSettings'),
      (snap) => {
        if (snap.exists()) {
          setOrgSettings({ id: snap.id, ...snap.data() });
        } else {
          // Doc not yet created. We'll let the admin's first-login backfill
          // create it. Until then, treat as "all apps enabled" so existing
          // users aren't surprised by sudden missing apps. The fallback is
          // baked into userCanAccessApp via the null-orgSettings branch.
          setOrgSettings(null);
        }
      },
      (err) => {
        console.warn('Org settings snapshot failed (treating as all-enabled):', err);
        setOrgSettings(null);
      }
    );
    return () => unsub();
  }, [user]);

  // Set page title during loading based on URL
  useEffect(() => {
    if (authLoading) {
      const path = window.location.pathname;
      if (path.startsWith('/defect-tracker')) {
        document.title = 'Loading... - Defect Tracker';
      } else if (path.startsWith('/feature-requests')) {
        document.title = 'Loading... - Feature Requests';
      } else if (path.startsWith('/messaging')) {
        document.title = 'Loading... - Messaging';
      } else if (path.startsWith('/notes')) {
        document.title = 'Loading... - Notes';
      } else if (path.startsWith('/diagramming')) {
        document.title = 'Loading... - Diagramming';
      } else if (path.startsWith('/desktop')) {
        document.title = 'Loading... - Desktop';
      } else if (path.startsWith('/dashboard')) {
        document.title = 'Loading... - Dashboard';
      }
    }
  }, [authLoading]);

  if (authLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={!user ? <SuiteHome /> : <Navigate to="/desktop" />} />
        <Route path="/signin" element={!user ? <Login mode="signin" /> : <Navigate to="/desktop" />} />
        <Route path="/signup" element={!user ? <Login mode="signup" /> : <Navigate to="/desktop" />} />

        {/* Protected Routes */}
        <Route 
          path="/desktop" 
          element={user ? <Desktop /> : <Navigate to="/signin" />} 
        />
        <Route 
          path="/dashboard" 
          element={user ? <SuiteDashboard user={user} userProfile={userProfile} orgSettings={orgSettings} /> : <Navigate to="/signin" />} 
        />

        {/* Suite apps — gated by org enabled list AND per-user assignedSuiteApps */}
        <Route 
          path="/defect-tracker" 
          element={user && userProfile ? (
            <AppAccessGate appId="defect-tracker" userProfile={userProfile} orgSettings={orgSettings}>
              <DefectTrackerApp />
            </AppAccessGate>
          ) : <Navigate to="/signin" />} 
        />
        <Route 
          path="/defect-tracker/:defectId" 
          element={user && userProfile ? (
            <AppAccessGate appId="defect-tracker" userProfile={userProfile} orgSettings={orgSettings}>
              <DefectTrackerApp />
            </AppAccessGate>
          ) : <Navigate to="/signin" />} 
        />
        <Route 
          path="/feature-requests" 
          element={user && userProfile ? (
            <AppAccessGate appId="feature-requests" userProfile={userProfile} orgSettings={orgSettings}>
              <FeatureRequestsWrapper />
            </AppAccessGate>
          ) : <Navigate to="/signin" />} 
        />
        <Route 
          path="/feature-requests/:requestId" 
          element={user && userProfile ? (
            <AppAccessGate appId="feature-requests" userProfile={userProfile} orgSettings={orgSettings}>
              <FeatureRequestsWrapper />
            </AppAccessGate>
          ) : <Navigate to="/signin" />} 
        />
        <Route 
          path="/notes" 
          element={user && userProfile ? (
            <AppAccessGate appId="notes" userProfile={userProfile} orgSettings={orgSettings}>
              <NotesWrapper />
            </AppAccessGate>
          ) : <Navigate to="/signin" />} 
        />
        {/* Build 26: path-param permalink for individual notes (used by
            Copy Link button). Backward-compat: /notes?noteId=... still works. */}
        <Route 
          path="/notes/:noteId" 
          element={user && userProfile ? (
            <AppAccessGate appId="notes" userProfile={userProfile} orgSettings={orgSettings}>
              <NotesWrapper />
            </AppAccessGate>
          ) : <Navigate to="/signin" />} 
        />
        <Route 
          path="/messaging" 
          element={user && userProfile ? (
            <AppAccessGate appId="messaging" userProfile={userProfile} orgSettings={orgSettings}>
              <MessagingWrapper />
            </AppAccessGate>
          ) : <Navigate to="/signin" />} 
        />
        <Route 
          path="/diagramming" 
          element={user && userProfile ? (
            <AppAccessGate appId="diagramming" userProfile={userProfile} orgSettings={orgSettings}>
              <DiagrammingApp />
            </AppAccessGate>
          ) : <Navigate to="/signin" />} 
        />
        <Route 
          path="/incident-reports" 
          element={user && userProfile ? (
            <AppAccessGate appId="incident-reports" userProfile={userProfile} orgSettings={orgSettings}>
              <IncidentReportsApp />
            </AppAccessGate>
          ) : <Navigate to="/signin" />} 
        />
        <Route 
          path="/injury-reports" 
          element={user && userProfile ? (
            <AppAccessGate appId="injury-reports" userProfile={userProfile} orgSettings={orgSettings}>
              <InjuryReportsApp />
            </AppAccessGate>
          ) : <Navigate to="/signin" />} 
        />
        <Route 
          path="/maintenance-requests" 
          element={user && userProfile ? (
            <AppAccessGate appId="maintenance-requests" userProfile={userProfile} orgSettings={orgSettings}>
              <MaintenanceRequestsApp />
            </AppAccessGate>
          ) : <Navigate to="/signin" />} 
        />
        <Route 
          path="/sop-system" 
          element={user && userProfile ? (
            <AppAccessGate appId="sop-system" userProfile={userProfile} orgSettings={orgSettings}>
              <SopSystemApp />
            </AppAccessGate>
          ) : <Navigate to="/signin" />} 
        />
        <Route 
          path="/account-requests" 
          element={<AccountRequestsApp />} 
        />

        {/* Build 25: admin routes — guarded inside the components themselves */}
        <Route
          path="/admin"
          element={user && userProfile ? (
            <AdminHub user={user} userProfile={userProfile} />
          ) : <Navigate to="/signin" />}
        />
        <Route
          path="/admin/users"
          element={user && userProfile?.role === 'Admin' ? (
            <AdminPanel user={user} onBack={() => window.history.back()} />
          ) : <Navigate to="/dashboard" />}
        />
        <Route
          path="/admin/apps"
          element={user && userProfile?.role === 'Admin' ? (
            <ApplicationManager user={user} onBack={() => window.history.back()} />
          ) : <Navigate to="/dashboard" />}
        />
        {/* Build 25.1: org-wide settings + audit log */}
        <Route
          path="/admin/org-settings"
          element={user && userProfile?.role === 'Admin' ? (
            <OrgSettings user={user} userProfile={userProfile} />
          ) : <Navigate to="/dashboard" />}
        />
        <Route
          path="/admin/org-settings/audit"
          element={user && userProfile?.role === 'Admin' ? (
            <OrgSettingsAudit user={user} userProfile={userProfile} />
          ) : <Navigate to="/dashboard" />}
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      
      {/* Help Bot - Available on all pages when authenticated */}
      {user && <HelpBot user={user} />}
    </BrowserRouter>
  );
}

export default Suite;
